/**
 * Les métadonnées minimales à joindre à un compte qu'on ajoute en bibliothèque.
 *
 * ## Pourquoi ce n'est pas facultatif
 *
 * `LibraryItem.sourceId` ne suffit pas aux écrans : chaque famille de connecteur relit
 * l'identifiant **dans `metadata`, sous son propre nom**. Le dashboard Google Ads interroge
 * `metadata.customerId` et laisse tomber la source quand il est absent
 * (`routes/dashboard/networks.js`) ; les analytics lisent `propertyId` ou `siteId`, Search
 * Console `siteUrl`, Merchant Center `merchantId`.
 *
 * Un compte ajouté avec `metadata: {}` s'enregistre donc **sans erreur**, s'affiche
 * normalement dans la liste des comptes, et ne rend aucune donnée — avec pour seul indice un
 * « Invalid customer ID 'undefined' » au fond d'un bandeau d'avertissement. C'est exactement ce
 * qui s'est produit : le défaut ne se voit qu'au premier dashboard ouvert.
 *
 * ## Une règle, plusieurs copies
 *
 * Elle existait déjà en deux endroits — dans chaque sélecteur de l'écran Comptes (`App.js`) et,
 * sous forme condensée, dans l'assistant du Gestionnaire (`kanban/wizard/SetupWizard.jsx`). Ce
 * module en est la troisième écriture, et la seule à être réutilisable : les deux autres sont
 * enchâssées dans leur composant. Un connecteur ajouté devra être déclaré ici.
 *
 * Les sélecteurs de l'écran Comptes joignent en plus des informations de confort — devise,
 * fuseau, métriques disponibles — que seuls leurs appels d'aperçu connaissent. Elles ne sont
 * pas nécessaires à l'affichage des données ; ce module s'en tient à ce qui l'est.
 */

/**
 * Sous quel nom chaque famille de connecteur relit son identifiant.
 *
 * ⚠️ **Une valeur peut être une fonction, et non une simple chaîne.** La forme courte — une clé —
 * suppose qu'un identifiant suffit à désigner un compte. C'est vrai partout sauf chez Google Tag
 * Manager, dont **tous** les chemins d'API sont préfixés par `accounts/{accountId}/` : un
 * conteneur connu par son seul `containerId` n'est interrogeable par rien. La fonction reçoit en
 * plus le compte tel que l'a rendu `/api/connectors/:id/accounts`, et rend l'objet entier.
 */
const CLE_PAR_TYPE = {
  GOOGLE_ADS: 'customerId',
  MICROSOFT_ADS: 'accountId',
  FACEBOOK_ADS: 'accountId',
  PINTEREST_ADS: 'accountId',
  TIKTOK_ADS: 'accountId',
  OPENAI_ADS: 'accountId',
  AWIN: 'accountId',
  // PrestaShop range l'`id_shop` sous `idShop` : `sourceId` vaut `1` chez presque tout le
  // monde, et c'est le domaine, porté par le nom du compte, qui l'identifie dans le parc.
  PRESTASHOP: 'idShop',
  GOOGLE_ANALYTICS: 'propertyId',
  GOOGLE_SEARCH_CONSOLE: 'siteUrl',
  GOOGLE_MERCHANT_API: 'merchantId',
  MATOMO: 'siteId',
  MATOMO_OAUTH: 'siteId',
  PIANO_ANALYTICS: 'siteId',
  /*
   * ⚠️ **Deux identifiants, et le second n'est pas du confort.**
   *
   * `accountId` est le compte GTM qui héberge le conteneur, exigé par chacun des chemins de
   * l'API. Sans lui, `get_data` refuse en le nommant — ce qui vaut mieux qu'une liste vide, mais
   * reste un conteneur importé pour rien. Le backend le complète en dernier recours
   * (`routes/library.js`), parce qu'il y a trois chemins d'ajout et que chacun écrit ses
   * métadonnées à sa façon.
   *
   * `publicId` (`GTM-XXXXXXX`) est en revanche du confort assumé : c'est la forme que l'humain
   * reconnaît et recopie depuis l'interface, alors que l'API ne la consomme nulle part.
   */
  GOOGLE_TAG_MANAGER: (sourceId, compte = {}) => ({
    containerId: String(sourceId),
    accountId: compte.accountId !== undefined && compte.accountId !== null ? String(compte.accountId) : undefined,
    publicId: compte.publicId,
    containerName: compte.containerName || compte.name,
    accountName: compte.accountName,
    usageContext: compte.usageContext,
    tagManagerUrl: compte.tagManagerUrl
  })
};

/**
 * Google Ads écrit ses identifiants sans tirets.
 *
 * `569-643-2262` et `5696432262` désignent le même compte, mais l'API n'accepte que la seconde
 * forme : un identifiant ponctué y produit une erreur de compte introuvable.
 */
function normaliser(type, sourceId) {
  const valeur = String(sourceId ?? '');
  return type === 'GOOGLE_ADS' ? valeur.replace(/-/g, '') : valeur;
}

/**
 * Les métadonnées d'un compte de ce type.
 *
 * @param {string} type - type du connecteur (`GOOGLE_ADS`, `GOOGLE_ANALYTICS`…)
 * @param {string|number} sourceId
 * @returns {object} un objet vide si le type est inconnu — mieux vaut un compte sans métadonnées
 *   qu'une clé inventée, qu'aucun écran n'irait lire
 */
export function metadonneesDuCompte(type, sourceId, compte = {}) {
  const cle = CLE_PAR_TYPE[type];
  if (!cle) return {};

  // La forme longue : la famille a besoin de plus d'un identifiant pour être interrogeable.
  if (typeof cle === 'function') {
    const metadonnees = cle(normaliser(type, sourceId), compte);
    // Les clés sans valeur sont retirées plutôt que posées à `undefined` : `metadata` part en
    // JSON, et une clé explicitement nulle se lirait comme un réglage absent plutôt qu'absent
    // tout court — c'est le backend qui complète ce qui manque.
    return Object.fromEntries(
      Object.entries(metadonnees).filter(([, valeur]) => valeur !== undefined && valeur !== null)
    );
  }

  return { [cle]: normaliser(type, sourceId) };
}

export { CLE_PAR_TYPE };
export default metadonneesDuCompte;
